import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useQuery } from "@apollo/client/react";

import { EMPTY_CLAIM_MAPPING_VALUE } from "components/FormFields/ClaimMapping";
import Box from "ds/components/Box";
import FullScreenModal from "ds/components/FullScreenModal";
import { showSimpleLeaveConfirmation } from "ds/components/LeaveConfirmationModal/Simple";
import useTypedContext from "hooks/useTypedContext";
import { ApiKeyType } from "types/generated";
import { encodeApiKeyId } from "shared/ApiKey/urlHelpers";
import { AccountContext } from "views/AccountWrapper";

import { SettingsContext } from "../../Context";
import { UserManagementActivationStatus } from "../../types";
import { SPACE_ADMIN_ROLE_SLUG } from "../../constants";
import { ApiKeyCreationStep, API_KEY_CREATION_WIZARD_NAV_STEPS } from "./constants";
import { ApiKeyCreateWizardContext } from "./context";
import { USER_ROLES_FOR_API_KEY_CREATION } from "./gql";
import ApiKeyCreateStepper from "./Stepper";
import ApiKeyCreateStepDetails from "./Steps/Details";
import ApiKeyCreateStepManageAccess from "./Steps/ManageAccess";
import { CreateApiKeyFields } from "./types";

const defaultValues: CreateApiKeyFields = {
  description: "",
  name: "",
  groups: [],
  type: ApiKeyType.Secret,
  oidcClientId: "",
  oidcIssuer: "",
  oidcSubjectExpression: ".*",
  claimMapping: [EMPTY_CLAIM_MAPPING_VALUE],
  assignedRoles: [],
};

const ApiKeyCreate = () => {
  const [currentStep, setCurrentStep] = useState(ApiKeyCreationStep.Details);
  const navigate = useNavigate();
  const { activationStatus } = useTypedContext(SettingsContext);
  const { viewer } = useTypedContext(AccountContext);

  // Non-admins without user management have nothing to configure in the manage access step
  const hasManageAccessStep =
    viewer.admin || activationStatus === UserManagementActivationStatus.ACTIVE;

  const steps = useMemo(
    () =>
      hasManageAccessStep
        ? API_KEY_CREATION_WIZARD_NAV_STEPS
        : API_KEY_CREATION_WIZARD_NAV_STEPS.filter(
            ({ step }) => step !== ApiKeyCreationStep.ManageAccess
          ),
    [hasManageAccessStep]
  );

  const { data, loading: rolesLoading } = useQuery(USER_ROLES_FOR_API_KEY_CREATION);

  const availableRoles = useMemo(() => data?.roles || [], [data?.roles]);

  const spaceAdminRole = useMemo(() => {
    return availableRoles.find((role) => role.slug === SPACE_ADMIN_ROLE_SLUG && role.isSystem);
  }, [availableRoles]);

  const form = useForm<CreateApiKeyFields>({
    defaultValues,
    mode: "onChange",
  });

  const handleStepChange = useCallback((step: ApiKeyCreationStep) => {
    setCurrentStep(step);
  }, []);

  const goToNextStep = useCallback(() => {
    setCurrentStep((prevStep) => Math.min(steps.length - 1, prevStep + 1));
  }, [steps.length]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prevStep) => Math.max(0, prevStep - 1));
  }, []);

  const modalCloseClickHandler = useCallback(async () => {
    if (form.formState.isDirty) {
      await showSimpleLeaveConfirmation({
        title: "Do you want to leave API key creation process?",
        message: "Your changes will not be saved.",
      });
    }

    navigate("/settings/api-keys");
  }, [navigate, form.formState.isDirty]);

  const handleSuccess = useCallback(
    (id: string) => {
      navigate(`/settings/api-key/${encodeApiKeyId(id)}`);
    },
    [navigate]
  );

  const contextValue = useMemo(
    () => ({
      steps,
      currentStep,
      setCurrentStep: handleStepChange,
      goToNextStep,
      goToPreviousStep,
      isFirstStep: currentStep === steps[0].step,
      isLastStep: currentStep === steps[steps.length - 1].step,
      availableRoles,
      rolesLoading,
      spaceAdminRole,
      activationStatus,
      onClose: modalCloseClickHandler,
    }),
    [
      steps,
      currentStep,
      handleStepChange,
      goToNextStep,
      goToPreviousStep,
      availableRoles,
      rolesLoading,
      spaceAdminRole,
      activationStatus,
      modalCloseClickHandler,
    ]
  );

  return (
    <ApiKeyCreateWizardContext.Provider value={contextValue}>
      <FullScreenModal title="Create API key" onClose={modalCloseClickHandler}>
        <Box grow="1" fullWidth scrollable>
          <ApiKeyCreateStepper />
          <Box fullWidth direction="column" relative>
            <FormProvider {...form}>
              {currentStep === ApiKeyCreationStep.Details && (
                <ApiKeyCreateStepDetails onSuccess={handleSuccess} />
              )}
              {currentStep === ApiKeyCreationStep.ManageAccess && (
                <ApiKeyCreateStepManageAccess onSuccess={handleSuccess} />
              )}
            </FormProvider>
          </Box>
        </Box>
      </FullScreenModal>
    </ApiKeyCreateWizardContext.Provider>
  );
};

export default ApiKeyCreate;
